import {
  Activity,
  Building2,
  Camera,
  DollarSign,
  FileText,
  Heart,
  MessageSquare,
  Package,
  Radar,
  Shield,
  Target,
  Ticket,
  Trophy,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ClubModuleKey, ClubEnabledModules } from '@/types/club';
import type { ClubCapability } from '@/types/club-membership';

export type ClubModuleSectionTitle = 'Effectif' | 'Sportif' | 'Logistique' | 'Gestion';

export type ClubModuleDefinition = {
  key: ClubModuleKey;
  label: string;
  path: string;
  icon: LucideIcon;
  section: ClubModuleSectionTitle;
  /** Affiché à côté de l'interrupteur, pour que le choix se fasse en connaissance de cause. */
  description: string;
};

/**
 * Source unique des modules du club.
 *
 * Le menu du tableau de bord, la garde de route et les interrupteurs de la page
 * Administration lisent tous cette liste : une entrée ajoutée ici apparaît
 * partout, et aucun des trois ne peut diverger des deux autres.
 */
export const CLUB_MODULES: ClubModuleDefinition[] = [
  {
    key: 'members',
    label: 'Membres',
    path: '/club/members',
    icon: Users,
    section: 'Effectif',
    description: 'Joueurs, staff et contrats du club',
  },
  {
    key: 'teams',
    label: 'Équipes',
    path: '/club/teams',
    icon: Shield,
    section: 'Effectif',
    description: 'Catégories et compositions',
  },
  {
    key: 'parents',
    label: 'Parents',
    path: '/club/parents',
    icon: Heart,
    section: 'Effectif',
    description: 'Contacts des responsables légaux',
  },
  {
    key: 'recruitment',
    label: 'Recrutement',
    path: '/club/recrutement',
    icon: Radar,
    section: 'Effectif',
    description: 'Détection mondiale : une fiche d’essai par match, suivi des performances et pipeline de signature',
  },
  {
    key: 'trainings',
    label: 'Entraînements',
    path: '/club/trainings',
    icon: Activity,
    section: 'Sportif',
    description: 'Planification des séances',
  },
  // Le module « Matchs » est retiré du menu le temps de le retravailler : la clé
  // reste dans ClubModuleKey pour ne pas casser les préférences déjà stockées.
  {
    key: 'performance',
    label: 'Performance',
    path: '/club/performance',
    icon: Target,
    section: 'Sportif',
    description: 'Statistiques collectives et individuelles',
  },
  {
    key: 'medical',
    label: 'Médical',
    path: '/club/medical',
    icon: Heart,
    section: 'Sportif',
    description: 'Blessures et suivi médical',
  },
  {
    key: 'equipment',
    label: 'Matériel',
    path: '/club/equipment',
    icon: Package,
    section: 'Logistique',
    description: 'Inventaire des équipements',
  },
  {
    key: 'facilities',
    label: 'Installations',
    path: '/club/facilities',
    icon: Building2,
    section: 'Logistique',
    description: 'Terrains et infrastructures',
  },
  {
    key: 'communication',
    label: 'Communication',
    path: '/club/communication',
    icon: MessageSquare,
    section: 'Gestion',
    description: 'Annonces et messages au club',
  },
  {
    key: 'media',
    label: 'Médias',
    path: '/club/media',
    icon: Camera,
    section: 'Gestion',
    description: 'Médiathèque, publications et droit à l’image',
  },
  {
    key: 'finances',
    label: 'Finances',
    path: '/club/finances',
    icon: DollarSign,
    section: 'Gestion',
    description: 'Cotisations, dépenses et budget',
  },
  {
    key: 'ticketing',
    label: 'Billetterie',
    path: '/club/billetterie',
    icon: Ticket,
    section: 'Gestion',
    description: 'Événements, billets à QR code, guichet et recettes',
  },
];

export const CLUB_MODULE_SECTIONS: Array<{ title: ClubModuleSectionTitle; icon: LucideIcon }> = [
  { title: 'Effectif', icon: Users },
  { title: 'Sportif', icon: Trophy },
  { title: 'Logistique', icon: Package },
  { title: 'Gestion', icon: FileText },
];

/**
 * Administration reste toujours accessible : c'est de là qu'on rallume le reste.
 */
export const CLUB_ADMINISTRATION_LINK = {
  label: 'Administration',
  path: '/club/administration',
  icon: FileText,
};

/**
 * Contrats et fiches de paie.
 *
 * Hors modules eux aussi : un club ne les eteint pas, et l'entree s'affiche a
 * qui detient `contracts:read` — la direction et l'administration deleguee.
 */
export const CLUB_CONTRACTS_LINK = {
  label: 'Contrats & paie',
  path: '/club/contracts',
  icon: FileText,
};

/**
 * Photographes : annuaire et réservations pour les matchs du club.
 *
 * Hors modules : rien à éteindre, et l'entrée suit `matches:read` — qui voit
 * les matchs peut voir qui les couvre ; réserver exige `matches:write`.
 */
export const CLUB_PHOTOGRAPHERS_LINK = {
  label: 'Photographes',
  path: '/club/photographes',
  icon: Camera,
};

const MODULE_BY_PATH = new Map(CLUB_MODULES.map((module) => [module.path, module]));

/** Module correspondant à une URL, ou `null` si la page n'est pas gouvernée par un module. */
export function findClubModuleByPath(pathname: string): ClubModuleDefinition | null {
  return MODULE_BY_PATH.get(pathname) ?? null;
}

/**
 * Un module est actif tant qu'il n'a pas été explicitement éteint : un club dont
 * la fiche est antérieure aux modules garde donc son menu complet.
 */
export function isClubModuleEnabled(
  enabledModules: Partial<ClubEnabledModules> | undefined,
  key: ClubModuleKey
): boolean {
  return enabledModules?.[key] ?? true;
}

/**
 * Capacité de lecture qui ouvre chaque module.
 *
 * Un module activé pour le club ne l'est pas pour tout le monde : un joueur
 * n'a rien à faire dans les finances, quand bien même le club les utilise.
 * « Parents » relève de l'effectif, il n'a pas de capacité propre.
 */
const MODULE_READ_CAPABILITY: Record<ClubModuleKey, ClubCapability | ClubCapability[]> = {
  members: 'members:read',
  teams: 'teams:read',
  parents: 'members:read',
  // Le recrutement a ses propres capacités, et le serveur les exige
  // (`recruitment.controller`). Ouvrir le menu sur `members:read`, comme
  // c'était le cas, affichait une entrée qui renvoyait un 403 au staff, au
  // trésorier et au chargé de communication : promettre une page qu'on refuse
  // ensuite est pire que ne pas la montrer.
  recruitment: 'recruitment:read',
  trainings: 'trainings:read',
  matches: 'matches:read',
  performance: 'performance:read',
  medical: 'medical:read',
  equipment: 'equipment:read',
  facilities: 'facilities:read',
  communication: 'communication:read',
  media: 'communication:read',
  finances: 'finances:read',
  // Le vendeur n'a que « sell » et le contrôleur que « scan » : le menu doit
  // s'ouvrir pour chacun, sinon aucun ne peut atteindre son poste.
  ticketing: ['ticketing:read', 'ticketing:sell', 'ticketing:scan'],
};

/**
 * Le compte peut-il ouvrir ce module ?
 *
 * `capabilities` absent signifie une fiche club obtenue hors session de
 * gestion : on ne restreint alors rien côté interface, le serveur tranchera.
 */
export function canAccessClubModule(
  capabilities: ClubCapability[] | undefined,
  key: ClubModuleKey
): boolean {
  if (!capabilities || capabilities.length === 0) return true;
  const required = MODULE_READ_CAPABILITY[key];
  const accepted = Array.isArray(required) ? required : [required];
  return accepted.some((capability) => capabilities.includes(capability));
}

/**
 * Ce qu'un membre vient faire dans un module, selon ce que son rôle lui ouvre.
 *
 * Rangés du plus engageant au plus simple : le premier droit que le membre
 * détient donne le verbe. Sans cela, tout le monde lisait « Consulter », y
 * compris le vendeur de billets dont le module est précisément le guichet.
 */
const MODULE_ACTIONS: Record<ClubModuleKey, Array<{ capability: ClubCapability; label: string }>> =
  {
    members: [{ capability: 'members:write', label: 'Gérer' }],
    teams: [
      { capability: 'teams:admin', label: 'Gérer' },
      { capability: 'teams:write', label: 'Composer' },
    ],
    parents: [{ capability: 'members:write', label: 'Gérer' }],
    recruitment: [
      { capability: 'recruitment:decide', label: 'Décider' },
      { capability: 'recruitment:write', label: 'Alimenter' },
    ],
    trainings: [{ capability: 'trainings:write', label: 'Planifier' }],
    matches: [{ capability: 'matches:write', label: 'Renseigner' }],
    performance: [{ capability: 'performance:write', label: 'Saisir' }],
    medical: [{ capability: 'medical:write', label: 'Gérer' }],
    equipment: [{ capability: 'equipment:write', label: 'Gérer' }],
    facilities: [{ capability: 'facilities:write', label: 'Gérer' }],
    communication: [{ capability: 'communication:write', label: 'Publier' }],
    media: [{ capability: 'communication:write', label: 'Alimenter' }],
    finances: [{ capability: 'finances:write', label: 'Tenir' }],
    ticketing: [
      { capability: 'ticketing:write', label: 'Gérer' },
      { capability: 'ticketing:sell', label: 'Vendre' },
      { capability: 'ticketing:scan', label: 'Contrôler' },
    ],
  };

/** Le verbe du module pour ce membre, « Consulter » à défaut d'un droit d'écriture. */
export function moduleActionFor(
  capabilities: ClubCapability[] | undefined,
  key: ClubModuleKey
): string {
  if (!capabilities) return 'Consulter';
  const action = MODULE_ACTIONS[key].find((one) => capabilities.includes(one.capability));
  return action?.label ?? 'Consulter';
}

/** Le membre a-t-il un droit d'écriture sur ce module ? */
export function canWriteClubModule(
  capabilities: ClubCapability[] | undefined,
  key: ClubModuleKey
): boolean {
  if (!capabilities) return false;
  return MODULE_ACTIONS[key].some((one) => capabilities.includes(one.capability));
}

/**
 * Les modules qu'un membre peut réellement ouvrir : allumés par le club, et
 * ouverts par son rôle. C'est la liste que voit le menu du club, filtrée deux
 * fois — un tableau de bord qui promettrait autre chose mentirait.
 */
export function accessibleClubModules(
  capabilities: ClubCapability[] | undefined,
  enabledModules: ClubEnabledModules | undefined
): ClubModuleDefinition[] {
  return CLUB_MODULES.filter(
    (clubModule) =>
      isClubModuleEnabled(enabledModules, clubModule.key) &&
      canAccessClubModule(capabilities, clubModule.key)
  );
}
